// frontend/src/components/crm/CRMLayout.js
import React, { useState, useMemo, useCallback, useEffect, Suspense, useRef } from 'react';
import { Outlet, useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useFeatureRegistry } from '../../contexts/FeatureRegistryContext';
import { useSystemConfig } from '../../contexts/SystemConfigContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useMessagePolling } from '../../hooks/useMessagePolling';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Popover, PopoverTrigger, PopoverContent, PopoverAnchor } from '../ui/popover';
import { Card } from '../ui/card';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { useDialog, DialogHeader, DialogBody, DialogFooter } from '../kibo-ui/dialog';
import NotificationTrigger from '../ui/notification-trigger';
import CommandPalette from '../ui/command-palette';
import { GuidedTour, SmartDashboardCard, QuickActionButton, FloatingActionMenu } from '../ui/help-components';
import { PERMISSIONS, hasPermission } from '../../utils/permissions';
import { ErrorBoundary } from '../ui/error-boundary';
import toast from 'react-hot-toast';
import {
    HomeIcon,
    UserGroupIcon,
    BuildingOfficeIcon,
    ClipboardDocumentListIcon,
    DocumentDuplicateIcon,
    CalendarIcon,
    ChartBarIcon,
    ChatBubbleLeftRightIcon,
    DocumentTextIcon,
    BookOpenIcon,
    MapPinIcon,
    Cog6ToothIcon,
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    XMarkIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    UserPlusIcon,
    UsersIcon,
    PaperAirplaneIcon,
    CreditCardIcon,
    BoltIcon,
    LanguageIcon,
    CpuChipIcon,
    GlobeAltIcon,
    HeartIcon,
    SwatchIcon,
    BellIcon,
    CommandLineIcon,
    SunIcon,
    MoonIcon,
    ViewColumnsIcon,
    Squares2X2Icon,
    SparklesIcon,
    PlusIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    CheckCircleIcon,
    LockClosedIcon,
    EnvelopeIcon
} from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';
import MaintenancePage from './MaintenancePage';
import TrialStatusBanner from './TrialStatusBanner';
import CookieConsent from './CookieConsent';

// Skeleton component for loading states
function RouteSkeleton() {
    return (
        <div className="space-y-6 animate-pulse">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="space-y-4">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
                <div className="h-4 bg-muted rounded w-5/6"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-32 bg-muted rounded-lg"></div>
                ))}
            </div>
        </div>
    );
}

// Soft upsell popover for feature gates
function FeatureGate({ feature, requiredTier, children, hasFeature }) {
    const [open, setOpen] = useState(false);
    const unlocked = hasFeature(feature);
    const navigate = useNavigate();
    
    if (unlocked) return children;
    
    return (
        <div className="relative">
            <div
                className="opacity-50"
                role="button"
                tabIndex={0}
                aria-label={`Upgrade to ${requiredTier} to use this feature`}
                onClick={() => setOpen(true)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpen(true)}
            >
                {children}
            </div>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-72 rounded-lg border bg-popover p-3 shadow-lg z-50">
                    <p className="text-sm font-medium">Available on {requiredTier}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                        Unlock AI Copilot, Health Scoring, and automations tailored to relocation workflows.
                    </p>
                    <div className="mt-2 flex gap-2">
                        <Button size="sm" onClick={() => navigate('/billing')}>See plans</Button>
                        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Close</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Accordion component for collapsible navigation sections
function Accordion({ sections, defaultOpen, hasFeature, setSidebarOpen }) {
    // Get all section categories - memoized to avoid unnecessary recalculations
    const allCategories = useMemo(() => {
        return sections?.map(s => s.category).filter(Boolean) || [];
    }, [sections]);
    
    // Always expand all sections by default
    const [openSections, setOpenSections] = useState(() => {
        // Initialize with current sections, or empty array if not available yet
        return sections?.map(s => s.category).filter(Boolean) || [];
    });
    
    // Clear localStorage and ensure all sections are expanded whenever sections change
    useEffect(() => {
        localStorage.removeItem('crm-sidebar-sections');
        // Always set to all categories when sections are available
        if (allCategories.length > 0) {
            setOpenSections(allCategories);
        }
    }, [allCategories]);
    
    // Force expand all sections on mount (as a safety net)
    useEffect(() => {
        if (allCategories.length > 0) {
            setOpenSections(prev => {
                // Only update if we don't already have all categories
                const hasAllCategories = allCategories.every(cat => prev.includes(cat));
                return hasAllCategories ? prev : allCategories;
            });
        }
    }, []); // Run once on mount

    const toggleSection = (sectionName) => {
        const newOpenSections = openSections.includes(sectionName)
            ? openSections.filter(s => s !== sectionName)
            : [...openSections, sectionName];
        
        setOpenSections(newOpenSections);
        // Note: We don't save to localStorage anymore to ensure all sections are always expanded by default
    };

    return (
        <div className="space-y-1">
            {(sections || []).map((section) => {
                const isOpen = openSections.includes(section.category);
                return (
                        <div key={section.category}>
                            <button
                                onClick={() => toggleSection(section.category)}
                                aria-expanded={isOpen}
                                aria-controls={`section-${section.category}`}
                                className="w-full flex items-center justify-between px-2 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <span>{section.category}</span>
                                <ChevronDownIcon className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isOpen && (
                                <nav 
                                    id={`section-${section.category}`}
                                    role="navigation" 
                                    aria-label={section.category}
                                    className="space-y-1 mt-1"
                                >
                                {(section.items || []).map((item) => {
                                    const disabled = item.feature && !hasFeature(item.feature);
                                    // Simple path - basename handles /crm prefix automatically
                                    const to = `/${item.href}`;
                                    
                                    const navItem = (
                                        <NavItem
                                            key={item.name}
                                            to={to}
                                            icon={item.icon}
                                            label={item.name}
                                            badgeLabel={item.badgeLabel}
                                            badgeVariant={item.badgeVariant}
                                            badgeClassName={item.badgeClassName}
                                            disabled={disabled}
                                            onClick={() => setSidebarOpen(false)}
                                        />
                                    );
                                    
                                    return item.feature ? (
                                        <FeatureGate key={item.name} feature={item.feature} requiredTier={item.requiredTier} hasFeature={hasFeature}>
                                            {navItem}
                                        </FeatureGate>
                                    ) : (
                                        navItem
                                    );
                                })}
                            </nav>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// NavItem component for accurate active states and accessibility
function NavItem({ to, icon: Icon, label, disabled, badgeLabel, badgeVariant = 'secondary', badgeClassName = '', onClick }) {
    return (
        <div className="flex items-center">
            <NavLink
                to={to}
                aria-disabled={disabled}
                onClick={onClick}
                className={({ isActive }) =>
                    cn(
                        "group flex w-full items-center rounded-lg h-11 px-3 transition-colors",
                        disabled && "opacity-50 cursor-not-allowed pointer-events-none",
                        isActive
                            ? "bg-primary/10 text-primary border border-primary/20 shadow-sm"
                            : "hover:bg-muted hover:text-foreground"
                    )
                }
                end
            >
                <Icon className="h-4 w-4 mr-3 shrink-0" />
                <span className="flex-1 text-left truncate">{label}</span>
                {badgeLabel && (
                    <Badge variant={badgeVariant} className={cn("ml-2 text-[10px]", badgeClassName)}>
                        {badgeLabel}
                    </Badge>
                )}
            </NavLink>
        </div>
    );
}

// Breadcrumb component for better navigation context
function Breadcrumbs({ currentTitle }) {
    const navigate = useNavigate();
    const location = useLocation();
    
    const getBreadcrumbPath = () => {
        // Only show breadcrumbs for detail pages, not main section pages
        if (location.pathname.includes('/clients/') && !location.pathname.endsWith('/clients')) {
            return ['Clients', 'Details'];
        }
        if (location.pathname.includes('/tasks/') && !location.pathname.endsWith('/tasks')) {
            return ['Tasks', 'Details'];
        }
        if (location.pathname.includes('/relocation-workspace/')) {
            return ['Clients', 'Relocation Workspace'];
        }
        // For main section pages, don't show breadcrumbs to avoid duplication
        return [];
    };
    
    const breadcrumbs = getBreadcrumbPath();
    
    // Don't render breadcrumbs for main section pages
    if (breadcrumbs.length === 0) {
        return null;
    }
    
    return (
        <nav aria-label="Breadcrumb" className="hidden sm:flex items-center text-sm text-muted-foreground">
            {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <span className="mx-2">/</span>}
                    {index === breadcrumbs.length - 1 ? (
                        <span className="text-foreground font-medium">{crumb}</span>
                    ) : (
                        <span 
                            className="hover:text-foreground cursor-pointer"
                            onClick={() => {
                                if (crumb === 'Clients') navigate('/clients');
                                else if (crumb === 'Tasks') navigate('/tasks');
                                else navigate('/dashboard');
                            }}
                        >
                            {crumb}
                        </span>
                    )}
                </React.Fragment>
            ))}
        </nav>
    );
}

const CRMLayout = () => {
    const { user, currentAgency, agencies, loading, logout, switchAgency, createAgency, canCreateAgency, getRemainingAgencySlots, forceRefresh } = useCRM();
    const { branding } = useBranding();
    const { subscription, hasFeature } = useSubscription();
    const { isFeatureLive, getFeatureStatus } = useFeatureRegistry();
    const { openDialog, closeDialog } = useDialog();
    const { banner, maintenance } = useSystemConfig();
    const { createNotification } = useNotifications();
    const navigate = useNavigate();
    const location = useLocation();
    
    // IMPORTANT: All hooks must be called before any conditional returns
    // Set up global message polling (runs even when MessageList is not mounted)
    useMessagePolling();
    
    // All state hooks must be called before any early returns
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [darkMode, setDarkMode] = useState(() => {
        const stored = localStorage.getItem('crm-theme');
        if (stored) return stored === 'dark';
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    });
    const [showAgencyDropdown, setShowAgencyDropdown] = useState(false);
    const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
    const [density, setDensity] = useState(() => {
        const stored = localStorage.getItem('crm-density');
        return stored || 'cozy';
    });
    const [showGuidedTour, setShowGuidedTour] = useState(false);
    const [showFloatingMenu, setShowFloatingMenu] = useState(false);
    const triggerRef = useRef(null);
    
    // IMPORTANT: All hooks (including useMemo) must be called before any conditional returns
    // Determine if current user is a client-only user for current agency
    const isSuperadmin = !!user?.isSuperadmin;

    const isClientUser = useMemo(() => {
        if (!user || !Array.isArray(user.agencyMemberships) || !currentAgency) return false;
        if (user.isSuperadmin) return false;
        const membership = user.agencyMemberships.find(m => String(m.agencyId) === String(currentAgency._id) || String(m.agencyId?._id) === String(currentAgency._id));
        return membership?.role === 'client';
    }, [user, currentAgency]);

    const bannerSeverity = banner?.severity || 'info';
    const bannerVariants = {
        info: 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-200',
        success: 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-100',
        warning: 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-100',
        danger: 'bg-red-50 border-red-200 text-red-900 dark:bg-red-950/40 dark:border-red-800 dark:text-red-100'
    };
    const bannerIconMap = {
        info: InformationCircleIcon,
        success: CheckCircleIcon,
        warning: ExclamationTriangleIcon,
        danger: ExclamationTriangleIcon
    };
    const BannerIcon = bannerIconMap[bannerSeverity] || InformationCircleIcon;

    // Whether user has only client memberships across all agencies
    const hasOnlyClientMemberships = useMemo(() => {
        if (!user || !Array.isArray(user.agencyMemberships)) return false;
        if (user.isSuperadmin) return false;
        return user.agencyMemberships.length > 0 && user.agencyMemberships.every(m => m.role === 'client');
    }, [user]);

    // Check if user has no agencies (and is not superadmin)
    const hasNoAgencies = useMemo(() => {
        return !isSuperadmin && (!agencies || agencies.length === 0);
    }, [agencies, isSuperadmin]);

    const hasAgencies = Array.isArray(agencies) && agencies.length > 0;
    const showCreateAgencyCTA = !isClientUser && !hasAgencies;

    // Compute a safe display name for the user to avoid "undefined undefined"
    const displayName = useMemo(() => {
        let composed = user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(' ');
        if ((!composed || composed.toLowerCase() === 'undefined undefined') && isClientUser) {
            try {
                const portalName = localStorage.getItem('client_display_name');
                if (portalName) composed = portalName;
            } catch (_) {}
        }
        const name = (composed || '').trim();
        if (!name || name.toLowerCase() === 'undefined undefined') {
            if (user?.email && typeof user.email === 'string') {
                return user.email.split('@')[0];
            }
            return 'User';
        }
        return name;
    }, [user, isClientUser]);

    // Internationalization support
    const locale = currentAgency?.locale || navigator.language || 'es-ES';
    const today = new Date().toLocaleDateString(locale, { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    });

    // Navigation items organized by category
    const navigation = useMemo(() => {
        const sections = [
        {
            category: 'Overview',
            items: [
                { name: 'Dashboard', href: 'dashboard', icon: HomeIcon },
                { name: 'Consultant Mode', href: 'consultant-mode', icon: SparklesIcon, feature: 'consultantMode' },
            ]
        },
        {
            category: 'CRM Core',
            items: [
                { name: 'Leads', href: 'leads', icon: UserPlusIcon },
                { name: 'Clients', href: 'clients', icon: UserGroupIcon },
                { name: 'Accounts', href: 'accounts', icon: BuildingOfficeIcon },
                { name: 'Tasks', href: 'tasks', icon: ClipboardDocumentListIcon },
            ]
        },
        {
            category: 'AI & Intelligence',
            items: [
                { name: 'AI Copilot', href: 'ai-copilot', icon: CpuChipIcon, feature: 'aiCopilot', requiredTier: 'Enterprise' },
                { name: 'Engagement Health', href: 'engagement-health', icon: HeartIcon, feature: 'engagementHealth', requiredTier: 'Professional' },
                { name: 'Collaboration Hub', href: 'collaboration', icon: ChatBubbleLeftRightIcon, feature: 'collaborationHub', requiredTier: 'Enterprise' },
            ]
        },
        {
            category: 'Communication',
            items: [
                { name: 'Campaigns', href: 'campaigns', icon: PaperAirplaneIcon, feature: 'emailCampaigns', requiredTier: 'Enterprise' },
                { name: 'Sending Profiles', href: 'sending-profiles', icon: Cog6ToothIcon, feature: 'emailCampaigns', requiredTier: 'Enterprise' },
                    { name: 'Email Templates', href: 'email-templates', icon: EnvelopeIcon, feature: 'aiCopilot' },
                    { name: 'Calendar', href: 'calendar', icon: CalendarIcon, feature: 'calendar' },
            ]
        },
        {
            category: 'Automation',
            items: [
                    { name: 'Workflows', href: 'workflows', icon: BoltIcon, feature: 'workflows' },
                    { name: 'Task Automation', href: 'task-automation', icon: CpuChipIcon, feature: 'taskAutomation', requiredTier: 'Professional' },
                { name: 'Relocation Workflows', href: 'relocation-workflows', icon: SparklesIcon, feature: 'relocationWorkflows' },
            ]
        },
        {
            category: 'Content & Documents',
            items: [
                { name: 'Documents', href: 'documents', icon: DocumentTextIcon, feature: 'documentManagement', requiredTier: 'Starter' },
                { name: 'Locations', href: 'locations', icon: MapPinIcon, feature: 'locationMaps', requiredTier: 'Starter' },
                    { name: 'Forms', href: 'forms', icon: ClipboardDocumentListIcon, feature: 'forms' },
                    { name: 'Insights', href: 'insights', icon: BookOpenIcon, feature: 'insights' },
            ]
        },
        {
            category: 'Analytics & Finance',
            items: [
                    { name: 'Analytics', href: 'analytics', icon: ChartBarIcon, feature: 'advancedAnalytics', requiredTier: 'Professional' },
                    { name: 'Billing', href: 'billing', icon: CreditCardIcon, feature: 'billing' },
            ]
        },
        {
            category: 'Client Experience',
            items: [
                { name: 'Client Portal', href: 'client-portal', icon: GlobeAltIcon, feature: 'clientPortal', requiredTier: 'Professional' },
                { name: 'Relocation Journey', href: 'relocation-journey', icon: MapPinIcon, feature: 'relocationJourney' },
                { name: 'Relocation Workspace', href: 'relocation-workspace', icon: BuildingOfficeIcon, feature: 'relocationWorkspace' },
                    { name: 'Multi-language', href: 'multi-language', icon: LanguageIcon, feature: 'multiLanguage' },
            ]
        },
        {
            category: 'Settings',
            items: [
                { name: 'Agency Settings', href: 'agency-settings', icon: BuildingOfficeIcon },
            ]
        }
        ];

        if (user?.isSuperadmin) {
            sections.push({
                category: 'Administration',
                items: [
                    { name: 'Admin Control', href: 'admin', icon: CommandLineIcon },
                ]
            });
        }

        return sections
            .map(section => ({
                ...section,
                items: section.items
                    .map(item => {
                        const status = item.feature ? getFeatureStatus(item.feature) : null;
                        if (item.feature && (!isFeatureLive(item.feature) || status === 'hidden')) {
                            return null;
                        }
                        let badgeLabel = null;
                        let badgeVariant = 'secondary';
                        let badgeClassName = '';
                        if (status === 'beta') {
                            badgeLabel = 'Beta';
                            badgeVariant = 'outline';
                            badgeClassName = 'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-200';
                        } else if (status === 'new') {
                            badgeLabel = 'New';
                            badgeVariant = 'secondary';
                            badgeClassName = 'bg-sky-600 text-white dark:bg-sky-400 dark:text-slate-900';
                        }
                        return { ...item, status, badgeLabel, badgeVariant, badgeClassName };
                    })
                    .filter(Boolean)
            }))
            .filter(section => section.items.length > 0);
    }, [user?.isSuperadmin, isFeatureLive, getFeatureStatus]);

    // Permission-filtered navigation
    const filteredNavigation = useMemo(() => {
        const currentAgencyId = currentAgency?._id || currentAgency?.id;
        const can = (p) => hasPermission(user, p, currentAgencyId);
        const map = {
            leads: PERMISSIONS.VIEW_CLIENTS, // Use VIEW_CLIENTS permission for leads
            clients: PERMISSIONS.VIEW_CLIENTS,
            accounts: PERMISSIONS.VIEW_ACCOUNTS,
            tasks: PERMISSIONS.VIEW_TASKS,
            'email-templates': PERMISSIONS.VIEW_TEMPLATES,
            'email-campaigns': PERMISSIONS.MANAGE_EMAIL_CAMPAIGNS,
            'sending-profiles': PERMISSIONS.MANAGE_EMAIL_CAMPAIGNS,
            workflows: PERMISSIONS.MANAGE_TASKS,
            'task-automation': PERMISSIONS.MANAGE_TASKS,
            'agency-settings': PERMISSIONS.VIEW_AGENCY_SETTINGS,
            documents: PERMISSIONS.MANAGE_DOCUMENTS,
            locations: PERMISSIONS.MANAGE_CONTENT,
            insights: PERMISSIONS.VIEW_ANALYTICS,
            analytics: PERMISSIONS.VIEW_ANALYTICS,
            billing: PERMISSIONS.MANAGE_AGENCY,
            'multi-language': PERMISSIONS.MANAGE_CONTENT,
        };
        return navigation
            .map(section => ({
                ...section,
                items: section.items.filter(item => {
                    const required = map[item.href];
                    return required ? can(required) : true;
                })
            }))
            .filter(section => section.items.length > 0);
    }, [navigation, user, currentAgency]);

    // Compute current title with useMemo for performance
    const currentTitle = useMemo(() => {
        // Check pathname with and without /crm prefix (for basename compatibility)
        const pathWithoutCrm = location.pathname.replace(/^\/crm/, '');
        const currentItem = navigation
            .flatMap(section => section.items)
            .find(item => location.pathname.includes(`/${item.href}`) || pathWithoutCrm.includes(`/${item.href}`));
        
        if (currentItem) {
            return currentItem.name;
        }
        
        // Special cases for better context
        if (location.pathname.includes('/clients/') && !location.pathname.endsWith('/clients')) {
            return 'Client Details';
        }
        if (location.pathname.includes('/tasks/') && !location.pathname.endsWith('/tasks')) {
            return 'Task Details';
        }
        if (location.pathname.includes('/relocation-workspace/')) {
            return 'Relocation Workspace';
        }
        
        return 'Dashboard';
    }, [navigation, location.pathname]);

    const handleLogout = useCallback(() => {
        logout();
        navigate('/login');
    }, [logout, navigate]);

    const handleSwitchAgency = useCallback(async (agencyId) => {
        try {
            // Ensure agencyId is a string
            const agencyIdStr = String(agencyId);
            console.log('🔄 Switching to agency:', agencyIdStr, 'Current agency:', currentAgency?._id);
            
            if (agencyIdStr === String(currentAgency?._id)) {
                console.log('⚠️ Already on this agency, skipping switch');
                setShowAgencyDropdown(false);
                return;
            }
            
            await switchAgency(agencyIdStr);
            navigate('/dashboard');
            setShowAgencyDropdown(false);
        } catch (error) {
            console.error('Failed to switch agency:', error);
            toast.error('Failed to switch agency');
        }
    }, [switchAgency, navigate, currentAgency]);

    const handleCreateAgency = useCallback(async () => {
        // Allow client-only users to start their first agency regardless of subscription limits
        if (!hasOnlyClientMemberships && !canCreateAgency()) {
            toast.error('You have reached the maximum number of agencies for your plan');
            return;
        }

        const dialogId = openDialog({
            isOpen: true,
            children: (
                <div role="dialog" aria-modal="true">
                    <DialogHeader title="Create New Agency" onClose={() => closeDialog(dialogId)} />
                    <DialogBody>
                <div className="space-y-4">
                    <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Agency Name</label>
                        <input
                            type="text"
                            id="agencyName"
                            className="w-full px-3 py-2 border border-input rounded-md b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                            placeholder="Enter agency name"
                        />
                    </div>
                    <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                        <textarea
                            id="agencyDescription"
                            rows={3}
                            className="w-full px-3 py-2 border border-input rounded-md b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                            style={{ '--tw-ring-color': branding.primaryColor }}
                            placeholder="Enter agency description"
                        />
                    </div>
                            {!hasOnlyClientMemberships && (
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>Remaining slots: {getRemainingAgencySlots()}</span>
                        <span>Current plan: {subscription?.tier || 'Free'}</span>
                    </div>
                            )}
                </div>
                    </DialogBody>
                    <DialogFooter>
                        <button
                            type="button"
                            className="px-3 py-2 text-sm border rounded-md"
                            onClick={() => closeDialog(dialogId)}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="px-3 py-2 text-sm text-white rounded-md"
                            style={{ backgroundColor: branding.primaryColor }}
                            onClick={async () => {
                        const name = document.getElementById('agencyName').value;
                        const description = document.getElementById('agencyDescription').value;
                                if (!name?.trim()) {
                            toast.error('Agency name is required');
                            return;
                        }
                        try {
                            const result = await createAgency({
                                name: name.trim(),
                                description: description.trim(),
                                motto: '',
                                email: user?.email || '',
                                phone: '',
                                address: '',
                                website: ''
                            });
                            if (result.success) {
                                toast.success('Agency created successfully!');
                                        try {
                                            // Refresh user/agencies to ensure membership is present
                                            if (typeof forceRefresh === 'function') {
                                                await forceRefresh();
                                            }
                                            const newId = result.data?._id || result.data?.id;
                                            if (newId) {
                                                await handleSwitchAgency(String(newId));
                                            }
                                        } catch (_) {}
                                setShowAgencyDropdown(false);
                                        closeDialog(dialogId);
                            } else {
                                toast.error(result.error || 'Failed to create agency');
                            }
                        } catch (error) {
                            console.error('Failed to create agency:', error);
                            toast.error('Failed to create agency');
                        }
                            }}
                        >
                            Create Agency
                        </button>
                    </DialogFooter>
                </div>
            )
        });
    }, [canCreateAgency, getRemainingAgencySlots, subscription, user, createAgency, openDialog, closeDialog, hasOnlyClientMemberships, branding.primaryColor, handleSwitchAgency, forceRefresh]);

    const toggleDarkMode = useCallback(() => {
        const newMode = !darkMode;
        setDarkMode(newMode);
        document.documentElement.classList.toggle('dark', newMode);
        localStorage.setItem('crm-theme', newMode ? 'dark' : 'light');
    }, [darkMode]);

    const toggleDensity = useCallback(() => {
        const newDensity = density === 'cozy' ? 'compact' : 'cozy';
        setDensity(newDensity);
        document.documentElement.setAttribute('data-density', newDensity);
        localStorage.setItem('crm-density', newDensity);
    }, [density]);

    const handleCommandPaletteAction = useCallback((action, data) => {
        if (action === 'navigate') {
            navigate(data);
        } else if (action === 'ai') {
            console.log('AI action:', data);
            // Handle AI actions
        }
        setCommandPaletteOpen(false);
    }, [navigate]);

    // Early authentication check - redirect to login if not authenticated
    // This must come AFTER all hooks are called
    const token = localStorage.getItem('crm_token');
    
    // Initialize dark mode and density on mount
    useEffect(() => {
        document.documentElement.classList.toggle('dark', darkMode);
        document.documentElement.setAttribute('data-density', density);
    }, [darkMode, density]);

    // Listen for system theme changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            if (!localStorage.getItem('crm-theme')) {
                setDarkMode(e.matches);
                document.documentElement.classList.toggle('dark', e.matches);
            }
        };
        
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    // Respect motion preferences
    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            document.documentElement.setAttribute('data-motion', 'reduce');
        }
    }, []);

    // Ensure agency context for client users (set current agency if missing)
    useEffect(() => {
        if (!isClientUser || !user) return;
        const stored = localStorage.getItem('current_agency_id');
        if (!stored) {
            const membership = (user.agencyMemberships || []).find(m => m.role === 'client');
            const agencyId = membership?.agencyId?._id || membership?.agencyId;
            if (agencyId) {
                localStorage.setItem('current_agency_id', String(agencyId));
                handleSwitchAgency(String(agencyId)).catch(() => {});
            }
        }
    }, [isClientUser, user, handleSwitchAgency]);

    // Close dropdown when clicking outside (mobile menu agency selector)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showAgencyDropdown) {
                const selector = event.target.closest('.agency-selector');
                // Close if clicking outside the agency selector
                if (!selector) {
                    setShowAgencyDropdown(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showAgencyDropdown]);

    // Handle escape key for agency dropdown
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && showAgencyDropdown) {
                setShowAgencyDropdown(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showAgencyDropdown]);

    // Return focus to trigger when dropdown closes
    useEffect(() => {
        if (!showAgencyDropdown && triggerRef.current) {
            triggerRef.current.focus();
        }
    }, [showAgencyDropdown]);

    // Add demo notifications for testing
    useEffect(() => {
        // Add a welcome notification on first load
        const hasSeenWelcome = localStorage.getItem('crm-welcome-notification');
        if (!hasSeenWelcome) {
            createNotification.success(
                'Welcome to LiveLoveSpain CRM!',
                'Your notification system is now active. Click the bell icon to manage notifications.',
                { action: () => navigate('/dashboard') }
            );
            localStorage.setItem('crm-welcome-notification', 'true');
        }
    }, [createNotification, navigate]);

    // Command Palette keyboard shortcut
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Command palette shortcuts
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setCommandPaletteOpen(true);
            }
            
            // Alternative shortcut with /
            if (e.key === '/' && !e.metaKey && !e.ctrlKey) {
                // Avoid when typing into inputs
                if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
                e.preventDefault();
                setCommandPaletteOpen(true);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Auto-redirect client users into the Client Portal if they land on generic sections
    useEffect(() => {
        if (!isClientUser) return;
        const path = location.pathname;
        // Check path without /crm prefix since basename handles it
        const pathWithoutBasename = path.replace(/^\/crm/, '');
        const allowed = pathWithoutBasename.startsWith('/client-portal') || pathWithoutBasename.startsWith('/settings');
        if (!allowed) {
            navigate('/client-portal', { replace: true });
        }
    }, [isClientUser, location.pathname, navigate]);
    
    // Authentication check useEffect
    useEffect(() => {
        // Wait for CRM context to finish loading
        if (loading) return;
        
        // Check if user is authenticated
        if (!user && !token) {
            // No user and no token - redirect to login immediately
            // Simple path - basename handles /crm prefix automatically
            navigate('/login', { replace: true });
            return;
        }
        
        // If no user but token exists, authentication may have failed
        // Give a small delay for the API interceptor to handle it, then redirect if still no user
        if (!user && token) {
            const timeoutId = setTimeout(() => {
                if (!user) {
                    // Simple path - basename handles /crm prefix automatically
                    navigate('/login', { replace: true });
                }
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [user, loading, navigate, token]);
    
    // Show loading state while checking authentication or if not authenticated
    // This must come AFTER all hooks are called
    if (loading || (!user && !token)) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-300">Loading...</p>
                </div>
            </div>
        );
    }
    
    // If we have a token but no user yet, show loading (authentication in progress)
    if (token && !user) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-300">Verifying authentication...</p>
                </div>
            </div>
        );
    }

    // Allow superadmins and admin dashboard access even during maintenance
    // Check path without /crm prefix since basename handles it
    const pathWithoutBasename = location.pathname.replace(/^\/crm/, '');
    const isAdminRoute = pathWithoutBasename.startsWith('/admin') || location.pathname.startsWith('/crm/admin');
    if (maintenance?.enabled && !isSuperadmin && !isAdminRoute) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-muted/40 px-6 text-center">
                <div className="max-w-lg space-y-4">
                    <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto" aria-hidden="true" />
                    <h1 className="text-2xl font-semibold text-foreground">Scheduled Maintenance</h1>
                    <p className="text-muted-foreground">
                        {maintenance.message || 'We are performing scheduled maintenance. Please check back shortly.'}
                    </p>
                    <div className="flex justify-center gap-3">
                        <Button onClick={handleLogout} variant="outline">
                            Return to login
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Show maintenance page if maintenance mode is enabled (if not already handled above)
    // This is a fallback check - the main check is above
    if (maintenance?.enabled && !isSuperadmin && !isAdminRoute) {
        return <MaintenancePage message={maintenance.message} />;
    }

    // Banner severity color mapping
    const bannerColors = {
        info: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100',
        warning: 'bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800 text-yellow-900 dark:text-yellow-100',
        error: 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800 text-red-900 dark:text-red-100',
        success: 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 text-green-900 dark:text-green-100'
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Global Banner */}
            {banner?.enabled && banner?.message && (
                <div className={`fixed top-0 inset-x-0 z-[60] border-b ${bannerColors[banner.severity] || bannerColors.info}`}>
                    <div className="px-4 sm:px-6 lg:px-8 py-3">
                        <div className="flex items-center justify-center text-sm font-medium">
                            <InformationCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                            <span>{banner.message}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Global Masthead */}
            <header className={`fixed inset-x-0 z-50 h-14 bg-background/95 backdrop-blur border-b ${banner?.enabled ? 'top-[49px]' : 'top-0'}`}>
                <div className="h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between">
                    {/* Left section: Mobile menu + Brand */}
                    <div className="flex items-center gap-3">
                        {/* Mobile menu button (hidden when no agencies) */}
                        {!hasNoAgencies && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="lg:hidden" 
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                aria-label={sidebarOpen ? "Close navigation menu" : "Open navigation menu"}
                            >
                                {sidebarOpen ? (
                                <XMarkIcon className="h-5 w-5" />
                                ) : (
                                    <Bars3Icon className="h-5 w-5" />
                                )}
                            </Button>
                        )}

                        {/* Brand lockup - Logo only on mobile, full brand on desktop */}
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="h-7 w-7 rounded-md grid place-items-center overflow-hidden">
                                <img 
                                    src="/LiveLoveSpain.png" 
                                    alt="LiveLoveSpain" 
                                    className="h-full w-full object-contain"
                                />
                            </div>
                            <span className="hidden lg:inline text-sm font-semibold truncate">LiveLoveSpain CRM</span>
                        </div>
                    </div>

                    {/* Center: Search bar - hidden on mobile */}
                    <div className="hidden md:flex flex-1 justify-center px-4">
                        <button
                            onClick={() => setCommandPaletteOpen(true)}
                            className="flex items-center gap-2 h-9 rounded-lg px-3 bg-muted/60 text-muted-foreground hover:text-foreground w-full max-w-[560px]"
                            title="Search or type /"
                        >
                            <span className="truncate text-sm">Search or type /</span>
                            <kbd className="ml-auto text-[11px]">⌘K</kbd>
                        </button>
                    </div>


                    <div className="ml-auto flex items-center gap-1">
                        {/* Admin Control button for superadmins (shown even when no agencies) */}
                        {isSuperadmin && hasNoAgencies && (
                            <button
                                onClick={() => navigate('/admin')}
                                className="hidden sm:flex items-center gap-2 h-9 px-3 rounded-lg hover:bg-muted transition-colors text-sm font-medium text-foreground"
                            >
                                <CommandLineIcon className="h-5 w-5" />
                                <span>Admin Control</span>
                            </button>
                        )}
                        {/* Agency display or switcher (hidden for client users) */}
                        {isClientUser ? (
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button className="hidden sm:flex items-center gap-2 h-9 px-3 rounded-lg hover:bg-muted transition-colors">
                                        {branding.agencyLogo ? (
                                            <img 
                                                key={`agency-logo-${currentAgency?._id || 'none'}`}
                                                src={branding.agencyLogo} 
                                                alt="" 
                                                className="h-5 w-5 rounded" 
                                            />
                                        ) : (
                                            <BuildingOfficeIcon className="h-5 w-5 text-muted-foreground" />
                                        )}
                                        {currentAgency?.name ? (
                                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                                <span className="text-sm truncate max-w-[200px]">
                                                    {currentAgency.name}
                                                </span>
                                                {(currentAgency.planTier || currentAgency.subscription?.plan) && (
                                                    <Badge variant="outline" className="text-[8px] px-1 py-0 leading-none flex-shrink-0">
                                                        {(currentAgency.planTier || currentAgency.subscription?.plan || 'trial').charAt(0).toUpperCase() + (currentAgency.planTier || currentAgency.subscription?.plan || 'trial').slice(1)}
                                                    </Badge>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                                        )}
                                        <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="!w-56 !p-2" align="end">
                                    <div className="space-y-1">
                                        {/* Current Agency - match agency user look */}
                                        {currentAgency && (
                                            <div className="p-2 rounded-lg" style={{ backgroundColor: `${branding.primaryColor}15` }}>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2 min-w-0 flex-1">
                                                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: branding.primaryColor }}></div>
                                                    <div className="flex flex-col items-start min-w-0 flex-1 min-h-[1.5rem]">
                                                        <span className="text-sm font-medium truncate" style={{ color: branding.primaryColor }}>
                                                            {currentAgency.name || 'Your Agency'}
                                                        </span>
                                                        {(currentAgency.planTier || currentAgency.subscription?.plan) && (
                                                            <Badge variant="outline" className="text-[8px] px-1 py-0 leading-none mt-0.5">
                                                                {(currentAgency.planTier || currentAgency.subscription?.plan || 'trial').charAt(0).toUpperCase() + (currentAgency.planTier || currentAgency.subscription?.plan || 'trial').slice(1)}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    </div>
                                                    <Badge variant="secondary" className="text-xs flex-shrink-0 ml-2">Current</Badge>
                                                </div>
                                            </div>
                                        )}

                                        {/* Other Agencies (allow switching even in client mode) */}
                                        {agencies?.filter(agency => String(agency._id) !== String(currentAgency?._id)).map((agency) => (
                                            <button
                                                key={agency._id}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const agencyId = String(agency._id);
                                                    console.log('🖱️ Clicked agency:', agency.name, 'ID:', agencyId);
                                                    handleSwitchAgency(agencyId);
                                                }}
                                                className="w-full text-left p-2 hover:bg-muted rounded-lg transition-colors"
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                                                    <span className="text-sm text-foreground">{agency.name}</span>
                                                </div>
                                            </button>
                                        ))}
                                        {hasOnlyClientMemberships && (
                                            <>
                                                <div className="my-1 h-px bg-muted" />
                                                <button
                                                    onClick={handleCreateAgency}
                                                    className="w-full text-left p-2 hover:bg-muted rounded-lg transition-colors flex items-center space-x-2"
                                                >
                                                    <PlusIcon className="h-4 w-4" style={{ color: branding.secondaryColor }} />
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium" style={{ color: branding.secondaryColor }}>Start your agency</div>
                                                        <div className="text-xs text-muted-foreground">Create your workspace to manage your own clients</div>
                                                    </div>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </PopoverContent>
                            </Popover>
                        ) : showCreateAgencyCTA ? (
                            <button
                                onClick={handleCreateAgency}
                                className="hidden sm:inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-dashed border-secondary/70 text-secondary hover:bg-muted transition-colors"
                            >
                                <PlusIcon className="h-4 w-4" />
                                <span className="text-sm font-medium">Create Agency</span>
                            </button>
                        ) : (
                        <Popover>
                            <PopoverTrigger asChild>
                                <button className="hidden sm:flex items-center gap-2 h-9 px-3 rounded-lg hover:bg-muted transition-colors">
                                {branding.agencyLogo ? (
                                        <img 
                                            key={`agency-logo-${currentAgency?._id || 'none'}`}
                                            src={branding.agencyLogo} 
                                            alt="" 
                                            className="h-5 w-5 rounded" 
                                        />
                                    ) : (
                                        <BuildingOfficeIcon className="h-5 w-5 text-muted-foreground" />
                                    )}
                                    {currentAgency?.name ? (
                                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                            <span className="text-sm truncate max-w-[160px]">
                                                {currentAgency.name}
                                            </span>
                                            {(currentAgency.planTier || currentAgency.subscription?.plan) && (
                                                <Badge variant="outline" className="text-[8px] px-1 py-0 leading-none flex-shrink-0">
                                                    {(currentAgency.planTier || currentAgency.subscription?.plan || 'trial').charAt(0).toUpperCase() + (currentAgency.planTier || currentAgency.subscription?.plan || 'trial').slice(1)}
                                                </Badge>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                                    )}
                                    <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="!w-56 !p-2" align="end">
                                <div className="space-y-1">
                                    {currentAgency && (
                                        <div className="p-2 rounded-lg" style={{ backgroundColor: `${branding.primaryColor}15` }}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2 min-w-0 flex-1">
                                                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: branding.primaryColor }}></div>
                                                    <div className="flex flex-col items-start min-w-0 flex-1 min-h-[1.5rem]">
                                                        <span className="text-sm font-medium truncate" style={{ color: branding.primaryColor }}>
                                                            {currentAgency.name}
                                                        </span>
                                                        {(currentAgency.planTier || currentAgency.subscription?.plan) && (
                                                            <Badge variant="outline" className="text-[8px] px-1 py-0 leading-none mt-0.5">
                                                                {(currentAgency.planTier || currentAgency.subscription?.plan || 'trial').charAt(0).toUpperCase() + (currentAgency.planTier || currentAgency.subscription?.plan || 'trial').slice(1)}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                                <Badge variant="secondary" className="text-xs flex-shrink-0 ml-2">Current</Badge>
                                            </div>
                                        </div>
                                    )}
                                    {agencies?.filter(agency => agency._id !== currentAgency?._id).map((agency) => (
                                        <button
                                            key={agency._id}
                                            onClick={() => handleSwitchAgency(agency._id)}
                                            className="w-full text-left p-2 hover:bg-muted rounded-lg transition-colors"
                                        >
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                                                <span className="text-sm text-foreground">{agency.name}</span>
                                            </div>
                                        </button>
                                    ))}
                                    <div className="border-t pt-2 mt-2">
                                        {canCreateAgency() ? (
                                            <button
                                                onClick={handleCreateAgency}
                                                className="w-full text-left p-2 hover:bg-muted rounded-lg transition-colors flex items-center space-x-2"
                                            >
                                                <PlusIcon className="h-4 w-4" style={{ color: branding.secondaryColor }} />
                                                <span className="text-sm font-medium" style={{ color: branding.secondaryColor }}>
                                                    Add Agency
                                                </span>
                                            </button>
                                        ) : (
                                            <div className="p-2 text-center">
                                                <div className="flex items-center justify-center space-x-2 text-muted-foreground">
                                                    <LockClosedIcon className="h-4 w-4" />
                                                        <span className="text-xs">Upgrade to add more agencies</span>
                                                </div>
                                                    <div className="text-xs text-muted-foreground/70 mt-1">{getRemainingAgencySlots()} slots remaining</div>
                                            </div>
                                        )}
                            </div>
                        </div>
                            </PopoverContent>
                        </Popover>
                        )}

                            <NotificationTrigger variant="icon" />

                            {/* User Menu */}
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button className="flex items-center space-x-2 rounded-lg px-2 py-1 hover:bg-muted transition-colors">
                                        {user?.profileImage ? (
                                            <img
                                                src={user.profileImage}
                                                alt={displayName || 'User'}
                                                className="h-8 w-8 rounded-full object-cover border-2 border-border"
                                            />
                                        ) : displayName && displayName !== 'User' && displayName !== 'U' ? (
                                            <div className="h-8 w-8 bg-primary rounded-full flex items-center justify-center">
                                                <span className="text-xs font-medium text-primary-foreground">
                                                    {displayName.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="h-8 w-8 bg-muted rounded-full animate-pulse" />
                                        )}
                                        <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-56 p-1" align="end">
                                    <div className="px-3 py-2">
                                        <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                                        <p className="text-xs text-muted-foreground truncate">{user?.email || 'user@example.com'}</p>
                                    </div>
                                    <div className="my-1 h-px bg-muted" />
                                    <button
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted rounded flex items-center"
                                        onClick={() => setShowGuidedTour(true)}
                                    >
                                        <InformationCircleIcon className="h-4 w-4 mr-2" />
                                        Take a Tour
                                    </button>
                                    <div className="my-1 h-px bg-muted" />
                                    <div className="flex items-center justify-between px-3 py-2">
                                        <span className="text-sm">Dark mode</span>
                                        <Switch checked={darkMode} onCheckedChange={toggleDarkMode} aria-label="Toggle dark mode" />
                                    </div>
                                    <div className="my-1 h-px bg-muted" />
                                    <button
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted rounded"
                                        onClick={() => navigate('/settings')}
                                    >
                                        Profile & Security
                                    </button>
                                    <div className="my-1 h-px bg-muted" />
                                    <button
                                        className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded"
                                        onClick={handleLogout}
                                    >
                                        Log out
                                    </button>
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>
            </header>

            {/* Mobile sidebar backdrop (hidden when no agencies) */}
            {!hasNoAgencies && (
                <div 
                    className={cn(
                        "fixed inset-0 z-[100] lg:hidden transition-opacity duration-300",
                        sidebarOpen 
                            ? "bg-black/50 opacity-100 pointer-events-auto" 
                            : "bg-transparent opacity-0 pointer-events-none"
                    )}
                    onClick={() => setSidebarOpen(false)}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar (hidden for client users and when no agencies exist) */}
            {!isClientUser && !hasNoAgencies && (
            <div className={cn(
                "fixed inset-y-0 left-0 z-[110] w-72 bg-card border-r border-border",
                "transform transition-all duration-300 ease-out lg:translate-x-0",
                "flex flex-col shadow-xl lg:top-14",
                sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
            )}>
                {/* Mobile header with close button and agency selector */}
                <div className="lg:hidden border-b bg-muted/50">
                    <div className="flex items-center justify-between p-4">
                        <Button 
                            variant="ghost"
                            size="icon"
                            onClick={() => setSidebarOpen(false)}
                            aria-label="Close navigation menu"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </Button>
                        {/* Agency selector in mobile menu */}
                        {!isClientUser && hasAgencies && currentAgency && (
                            <div className="relative flex-1 max-w-[240px] ml-2 agency-selector">
                                <button 
                                    onClick={(e) => {
                                        // Simple toggle - accordion behavior
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowAgencyDropdown(prev => !prev);
                                    }}
                                    className="flex items-center gap-2 h-9 px-3 rounded-lg hover:bg-muted/80 transition-colors w-full"
                                >
                                    {branding.agencyLogo ? (
                                        <img 
                                            key={`agency-logo-mobile-${currentAgency?._id || 'none'}`}
                                            src={branding.agencyLogo} 
                                            alt="" 
                                            className="h-5 w-5 rounded flex-shrink-0" 
                                        />
                                    ) : (
                                        <BuildingOfficeIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                                    )}
                                    {currentAgency?.name ? (
                                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                            <span className="text-sm font-medium truncate text-left">
                                                {currentAgency.name}
                                            </span>
                                            {(currentAgency.planTier || currentAgency.subscription?.plan) && (
                                                <Badge variant="outline" className="text-[8px] px-1 py-0 leading-none flex-shrink-0">
                                                    {(currentAgency.planTier || currentAgency.subscription?.plan || 'trial').charAt(0).toUpperCase() + (currentAgency.planTier || currentAgency.subscription?.plan || 'trial').slice(1)}
                                                </Badge>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="h-4 w-24 bg-muted rounded animate-pulse flex-1" />
                                    )}
                                    {showAgencyDropdown ? (
                                        <ChevronUpIcon className="h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform" />
                                    ) : (
                                        <ChevronDownIcon className="h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform" />
                                    )}
                                </button>
                                {showAgencyDropdown && (
                                    <div className="absolute top-full right-0 mt-1 w-64 rounded-md border bg-popover p-2 text-popover-foreground shadow-md z-[120] animate-in fade-in-0 zoom-in-95">
                                    <div className="space-y-1">
                                        {currentAgency && (
                                        <div className="p-2 rounded-lg" style={{ backgroundColor: `${branding.primaryColor}15` }}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2 min-w-0 flex-1">
                                                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: branding.primaryColor }}></div>
                                                    <div className="flex flex-col items-start min-w-0 flex-1 min-h-[1.5rem]">
                                                        <span className="text-sm font-medium truncate" style={{ color: branding.primaryColor }}>
                                                            {currentAgency.name}
                                                        </span>
                                                        {(currentAgency.planTier || currentAgency.subscription?.plan) && (
                                                            <Badge variant="outline" className="text-[8px] px-1 py-0 leading-none mt-0.5">
                                                                {(currentAgency.planTier || currentAgency.subscription?.plan || 'trial').charAt(0).toUpperCase() + (currentAgency.planTier || currentAgency.subscription?.plan || 'trial').slice(1)}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                                <Badge variant="secondary" className="text-xs flex-shrink-0 ml-2">Current</Badge>
                                            </div>
                                        </div>
                                        )}
                                        {agencies?.filter(agency => String(agency._id) !== String(currentAgency?._id)).map((agency) => (
                                            <button
                                                key={agency._id}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const agencyId = String(agency._id);
                                                    console.log('🖱️ Clicked agency (mobile):', agency.name, 'ID:', agencyId);
                                                    handleSwitchAgency(agencyId);
                                                    setShowAgencyDropdown(false);
                                                    setSidebarOpen(false);
                                                }}
                                                className="w-full text-left p-2 hover:bg-muted rounded-lg transition-colors"
                                            >
                                                <div className="flex items-center space-x-2 min-w-0">
                                                    <div className="w-2 h-2 bg-gray-300 rounded-full flex-shrink-0"></div>
                                                    <span className="text-sm text-foreground truncate">{agency.name}</span>
                                                </div>
                                            </button>
                                        ))}
                                        <div className="border-t pt-2 mt-2">
                                            {canCreateAgency() ? (
                                                <button
                                                    onClick={() => {
                                                        handleCreateAgency();
                                                        setShowAgencyDropdown(false);
                                                        setSidebarOpen(false);
                                                    }}
                                                    className="w-full text-left p-2 hover:bg-muted rounded-lg transition-colors flex items-center space-x-2"
                                                >
                                                    <PlusIcon className="h-4 w-4 flex-shrink-0" style={{ color: branding.secondaryColor }} />
                                                    <span className="text-sm font-medium" style={{ color: branding.secondaryColor }}>
                                                        Add Agency
                                                    </span>
                                                </button>
                                            ) : (
                                                <div className="p-2 text-center">
                                                    <div className="flex items-center justify-center space-x-2 text-muted-foreground">
                                                        <LockClosedIcon className="h-4 w-4" />
                                                        <span className="text-xs">Upgrade to add more agencies</span>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground mt-1">{getRemainingAgencySlots()} slots remaining</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {!isClientUser && !hasAgencies && (
                            <div className="ml-2 flex-1 max-w-[240px]">
                                <button
                                    onClick={() => {
                                        handleCreateAgency();
                                        setSidebarOpen(false);
                                    }}
                                    className="w-full flex items-center justify-center gap-2 h-9 rounded-lg border border-dashed border-secondary/70 text-secondary hover:bg-muted transition-colors text-sm font-medium"
                                >
                                    <PlusIcon className="h-4 w-4" />
                                    Create Agency
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation */}
                <div className="flex-1 overflow-y-auto p-4 scrollbar-thin scroll-shadows">
                    <Accordion 
                        key={`accordion-${sidebarOpen}-${filteredNavigation?.length || 0}`}
                        sections={filteredNavigation} 
                        hasFeature={hasFeature} 
                        setSidebarOpen={setSidebarOpen} 
                    />
                </div>
            </div>
            )}

            {/* Main Content */}
            <div className={cn(!isClientUser && !hasNoAgencies ? "lg:pl-72" : "", banner?.enabled ? "pt-[63px]" : "pt-14", "flex flex-col min-h-screen")}>
                {/* Show "No Agency Found" screen when user has no agencies */}
                {hasNoAgencies ? (
                    <div className="flex items-center justify-center min-h-screen bg-background">
                        <div className="max-w-md w-full text-center p-8">
                            <div className="flex justify-center mb-6">
                                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 rounded-full">
                                    <BuildingOfficeIcon className="h-12 w-12 text-white" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-bold text-foreground mb-4">
                                No Agency Found
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                You're not currently part of any agency. You can create your own agency or wait to be invited to one.
                            </p>
                            <div className="space-y-3">
                                <button
                                    onClick={handleCreateAgency}
                                    className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity"
                                    style={{ backgroundColor: branding.primaryColor }}
                                >
                                    <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                                    Create Your Agency
                                </button>
                                <button
                                    onClick={() => navigate('/settings')}
                                    className="w-full inline-flex items-center justify-center px-6 py-3 border border-border rounded-lg shadow-sm text-sm font-medium text-foreground bg-background hover:bg-muted transition-colors"
                                >
                                    Go to Settings
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Trial Status Banner */}
                        <TrialStatusBanner />
                        {/* Page Content */}
                        <main id="main" className="flex-1 px-4 sm:px-6 lg:px-8 py-6 pb-16 lg:pb-6">
                            <ErrorBoundary>
                                <Suspense fallback={<RouteSkeleton />}>
                                    <Outlet />
                                </Suspense>
                            </ErrorBoundary>
                        </main>
                    </>
                )}
            </div>

            {/* Command Palette */}
            <CommandPalette
                isOpen={commandPaletteOpen}
                onClose={() => setCommandPaletteOpen(false)}
                onAction={handleCommandPaletteAction}
            />

            {/* Mobile utility bar (hidden for client users and when no agencies) */}
            {!isClientUser && !hasNoAgencies && (
            <div className="fixed bottom-0 inset-x-0 z-30 border-t bg-background p-2 flex justify-around lg:hidden">
                <Button size="icon" variant="ghost" onClick={() => navigate('/clients/new')} aria-label="Add client">
                    <PlusIcon className="h-5 w-5" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => navigate('/tasks/new')} aria-label="Add task">
                    <ClipboardDocumentListIcon className="h-5 w-5" />
                </Button>
            </div>
            )}

            {/* Floating Action Menu (hide on small screens and when no agencies) */}
            {!isClientUser && !hasNoAgencies && (
            <div className="hidden lg:block">
            <FloatingActionMenu
                isOpen={showFloatingMenu}
                onToggle={() => setShowFloatingMenu(!showFloatingMenu)}
                ariaLabel="Quick actions"
                direction="up"
                actions={[
                    {
                        icon: UserPlusIcon,
                        label: 'Add Client',
                        onClick: () => {
                            navigate('/clients/new');
                            setShowFloatingMenu(false);
                        }
                    },
                    {
                        icon: BuildingOfficeIcon,
                        label: 'Add Account',
                        onClick: () => {
                            navigate('/accounts/new');
                            setShowFloatingMenu(false);
                        }
                    },
                    {
                        icon: ClipboardDocumentListIcon,
                        label: 'Assign Task',
                        onClick: () => {
                            navigate('/tasks');
                            setShowFloatingMenu(false);
                        }
                    },
                ]}
            />
            </div>
            )}

            {/* Guided Tour */}
            <GuidedTour
                isActive={showGuidedTour}
                onComplete={() => setShowGuidedTour(false)}
                onSkip={() => setShowGuidedTour(false)}
                steps={[
                    {
                        title: "Welcome to LiveLoveSpain CRM!",
                        description: "This is your main dashboard where you'll manage all your relocation clients and tasks.",
                        action: "Click on 'Clients' in the sidebar to see your client list."
                    },
                    {
                        title: "Navigation Sidebar",
                        description: "Use the sidebar to navigate between different sections. The most important areas are Overview, CRM Core, and Client Experience.",
                        action: "Try clicking on 'Dashboard' to return to the main view."
                    },
                    {
                        title: "Quick Actions",
                        description: "Use the floating action button (bottom right) or the command palette (⌘K) to quickly create new clients or tasks.",
                        action: "Press ⌘K to open the command palette and try searching for 'create client'."
                    },
                    {
                        title: "Help & Support",
                        description: "Use the command palette (⌘K) for quick access to all features and actions. You can also take this guided tour anytime.",
                        action: "Press ⌘K to open the command palette and explore available commands."
                    }
                ]}
            />

            {/* Cookie Consent Banner */}
            <CookieConsent />
        </div>
    );
};

export default CRMLayout;